/**
 * Which task-section headers should render collapsed the moment a project's
 * tasks load — a section where every ROOT task is already done starts out of
 * the way, but a manual expand always sticks (callers own that state; this
 * helper only computes the one-shot seed).
 *
 * The "done" predicate here is deliberately the EXACT one the existing
 * done/total section badges already use (`secDone` in Projects.tsx,
 * `doneCount` in AccountDetail.tsx: status === "done" || status ===
 * "completed", counting root tasks only) — so a collapsed header's badge and
 * the fact that it collapsed can never disagree. Open sub-tasks are ignored
 * for the same reason: the badge doesn't count them either.
 */

export interface AutoCollapseTask {
  id?: string;
  status?: string | null;
  parent_task_id?: string | null;
  sub_tasks?: Array<{ id?: string; status?: string | null }> | null;
}

function isDone(status: string | null | undefined): boolean {
  return status === "done" || status === "completed";
}

/** Collect every task id referenced anywhere in a section's task list (root
 * ids and nested sub_tasks ids), so the exemption can match either shape. */
function collectAllTaskIds(tasks: AutoCollapseTask[]): Set<string> {
  const ids = new Set<string>();
  for (const task of tasks) {
    if (task.id) ids.add(task.id);
    if (task.sub_tasks) {
      for (const sub of task.sub_tasks) {
        if (sub.id) ids.add(sub.id);
      }
    }
  }
  return ids;
}

export function computeAutoCollapsedSectionIds(
  sections: Array<{ id: string }>,
  tasksBySectionId: Map<string, AutoCollapseTask[]>,
  exemptTaskId?: string | null,
): Set<string> {
  const collapsed = new Set<string>();

  for (const section of sections) {
    const tasks = tasksBySectionId.get(section.id);
    if (!tasks || tasks.length === 0) continue;

    // Root tasks only — defensive filter even though both surfaces' arrays
    // are already root-only per GET /api/projects/:id/tasks' contract.
    const roots = tasks.filter((t) => !t.parent_task_id);
    if (roots.length === 0) continue;

    const allDone = roots.every((t) => isDone(t.status));
    if (!allDone) continue;

    if (exemptTaskId) {
      const idsInSection = collectAllTaskIds(tasks);
      if (idsInSection.has(exemptTaskId)) continue;
    }

    collapsed.add(section.id);
  }

  return collapsed;
}
